import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Table,
  Pagination,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { MdFilterAlt, MdFilterAltOff } from "react-icons/md";
import Swal from "sweetalert2";
import { FaCalendar, FaTimes } from "react-icons/fa";
import { BsFiletypeXls } from "react-icons/bs";
import { RiFilterFill } from "react-icons/ri";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const API_URL = process.env.REACT_APP_API_URL;

function WithdrawalStatusRejectedLists() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchName, setSearchName] = useState("");
  const [searchAccount, setSearchAccount] = useState("");
  const [submittedSearchName, setSubmittedSearchName] = useState("");
  const [submittedSearchAccount, setSubmittedSearchAccount] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);

  const datePickerRef = useRef(null);
  const handleIconClick = () => {
    if (datePickerRef.current) datePickerRef.current.setOpen(true);
  };
  const handleClear = () => {
    setFilterFromDate(null);
  };

  const getAuthToken = () => localStorage.getItem("token");

  const handleToggle = () => setIsFilterActive(!isFilterActive);

  const formatDateForServer = (date) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const fetchVisits = async (
    page = 1,
    name = "",
    account = "",
    status = "",
  ) => {
    setLoading(true);
    const token = getAuthToken();

    const params = new URLSearchParams({
      page: page,
      limit: 10,
      name: name,
      account_number: account,
    });

    if (Array.isArray(status) && status.length > 0) {
      status.forEach((s) => params.append("status[]", s));
    } else if (status) {
      params.append("status", status);
    }

    if (filterFromDate) {
      params.append("start_date", formatDateForServer(filterFromDate));
    }
    if (filterToDate) {
      params.append("end_date", formatDateForServer(filterToDate));
    }

    const query = params.toString();

    try {
      const response = await axios.get(
        `${API_URL}/withdrawal-requests-rejected-list?${query}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = response.data;
      if (result.success === "1") {
        setVisits(result.data);
        setTotalPages(result.totalPages || 1);
        setCurrentPage(result.page || page);
      } else {
        setError(result.message || "Failed to fetch Rejected Data.");
        setVisits([]);
        setTotalPages(1);
      }
    } catch (err) {
      setError("Failed to fetch Rejected Data.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisits(
      currentPage,
      submittedSearchName,
      submittedSearchAccount,
      selectedStatus,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    submittedSearchName,
    submittedSearchAccount,
    selectedStatus,
  ]);

  const handleDateFilterSubmit = () => {
    if (filterFromDate && filterToDate) {
      const fromDate = new Date(formatDateForServer(filterFromDate));
      const toDate = new Date(formatDateForServer(filterToDate));
      if (fromDate > toDate) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Date Range",
          text: "From date cannot be greater than To date",
        });
        return;
      }
    }
    setCurrentPage(1);
    setSubmittedSearchName("");
    setSubmittedSearchAccount("");
    setSearchName("");
    setSearchAccount("");
    fetchVisits(1, "", "", selectedStatus);
  };

  const clearDateFilters = () => {
    setFilterFromDate("");
    setFilterToDate("");
    setCurrentPage(1);
    fetchVisits(1, submittedSearchName, submittedSearchAccount, selectedStatus);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSubmittedSearchName(searchName);
    setSubmittedSearchAccount(searchAccount);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
    setSearchName("");
    setSearchAccount("");
    setSubmittedSearchName("");
    setSubmittedSearchAccount("");
    setFilterFromDate("");
    setFilterToDate("");
    setCurrentPage(1);
  };

  const handleDownloadFilteredExcel = async () => {
    const token = localStorage.getItem("token");

    try {
      Swal.fire({
        title: "Please wait...",
        text: "Generating your filtered Excel file",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const params = new URLSearchParams({
        totalPages,
        start_date: formatDateForServer(filterFromDate),
        end_date: formatDateForServer(filterToDate),
      }).toString();

      const response = await fetch(
        `${API_URL}/download-withdrawal-reject-excel?${params}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) throw new Error("Failed to download Excel");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Filtered Withdrawal Rejected Report.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        icon: "success",
        title: "Download Successful!",
        text: "Filtered Excel report has been downloaded successfully.",
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (error) {
      console.error("Filtered Excel download failed:", error);
      Swal.fire({
        icon: "warning",
        title: "Download Failed!",
        text: "Please check the selected dates for downloading the report.",
      });
    }
  };

  // ---------------- LOADING ----------------
  if (loading && visits.length === 0) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // ---------------- ERROR ----------------
  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
        }}
      >
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  // ---------------- MAIN ----------------
  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                flexShrink: 0,
              }}
            >
              ❌
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                Commission Rejected List Details
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                View all rejected withdrawal requests
              </p>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <Button
                className="d-flex gap-2 align-items-center"
                variant="primary"
                onClick={handleDownloadFilteredExcel}
              >
                <BsFiletypeXls className="text-white fs-6" />
                Download
              </Button>

              <button
                className={`filter-toggle-btn btn ${
                  isFilterActive ? "active" : ""
                }`}
                onClick={handleToggle}
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "12px",
                  border: "1.5px solid #e2e8f0",
                  background: isFilterActive ? "#1e5799" : "#ffffff",
                  color: isFilterActive ? "#ffffff" : "#1e3a5f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  boxShadow: isFilterActive
                    ? "0 8px 20px rgba(30, 87, 153, 0.3)"
                    : "none",
                }}
                title={isFilterActive ? "Hide Filters" : "Show Filters"}
              >
                {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
              </button>
            </div>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background:
                  "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            <div style={{ padding: "30px" }}>
              {/* FILTER PANEL */}
              {isFilterActive && (
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1.5px dashed #cbd5e1",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "24px",
                  }}
                >
                  <Row className="gy-3">
                    <Col md={5} className="position-relative">
                      <div
                        className="position-absolute"
                        style={{
                          top: "8px",
                          right: "20px",
                          zIndex: 2,
                          cursor: "pointer",
                        }}
                      >
                        {!filterFromDate ? (
                          <FaCalendar onClick={handleIconClick} />
                        ) : (
                          <FaTimes onClick={handleClear} />
                        )}
                      </div>

                      <DatePicker
                        ref={datePickerRef}
                        selected={filterFromDate}
                        onChange={(date) => setFilterFromDate(date)}
                        placeholderText="From Date (DD-MM-YYYY)"
                        dateFormat="dd-MM-yyyy"
                        className="form-control w-100"
                        showPopperArrow={false}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </Col>

                    <Col md={5} className="position-relative">
                      <DatePicker
                        selected={filterToDate}
                        onChange={(date) => setFilterToDate(date)}
                        placeholderText="To Date (DD-MM-YYYY)"
                        dateFormat="dd-MM-yyyy"
                        className="form-control w-100"
                        showPopperArrow={false}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </Col>

                    <Col md={2} className="d-flex justify-content-start gap-2">
                      <Button
                        variant="primary"
                        onClick={handleDateFilterSubmit}
                      >
                        <RiFilterFill /> Filter
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}

              {/* TABLE */}
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Request Date</th>
                    <th>Withdraw ID</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>A/C Name</th>
                    <th>Bank</th>
                    <th>Branch Name</th>
                    <th>Account Number</th>
                    <th>IFSC Code</th>
                    <th>Request Amount</th>
                    <th>TDS %</th>
                    <th>TDS Amount</th>
                    <th>Net Amount (After TDS)</th>
                    <th>Advance Payment</th>
                    <th>Advance Payment Settled</th>
                    <th>Remaining Advance Payment</th>
                    <th>Net Payable Amount</th>
                    <th>Remark</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.length > 0 ? (
                    visits.map((visit, index) => (
                      <tr key={visit.id}>
                        <td>{(currentPage - 1) * 10 + index + 1}</td>
                        <td>
                          {new Date(visit.updated_at)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-")}
                        </td>
                        <td>{visit.withdraw_id}</td>
                        <td>
                          {visit.username
                            ? visit.username.charAt(0).toUpperCase() +
                              visit.username.slice(1).toLowerCase()
                            : ""}
                        </td>
                        <td>{visit.mobile}</td>
                        <td>
                          {visit.account_holder_name
                            ? visit.account_holder_name
                                .charAt(0)
                                .toUpperCase() +
                              visit.account_holder_name
                                .slice(1)
                                .toLowerCase()
                            : ""}
                        </td>
                        <td>
                          {visit.bank_name
                            ? visit.bank_name.charAt(0).toUpperCase() +
                              visit.bank_name.slice(1).toLowerCase()
                            : ""}
                        </td>
                        <td>{visit.bank_branch_name || "NA"}</td>
                        <td>{visit.account_number}</td>
                        <td>{visit.ifsc_code}</td>
                        <td>₹ {visit.amount || "0.00"}</td>
                        <td>{visit.tds_percent || "0.00"}</td>
                        <td>₹ {visit.tds || "0.00"}</td>
                        <td>₹ {visit.amount_after_tds || "0.00"}</td>
                        <td>₹ {visit.advance_payment || "0.00"}</td>
                        <td>₹ {visit.advance_payment_used || "0.00"}</td>
                        <td>
                          ₹{" "}
                          {visit.advance_payment -
                            visit.advance_payment_used || "0.00"}
                        </td>
                        <td>₹ {visit.net_payment || "0.00"}</td>
                        <td>{visit.remark || "NA"}</td>
                        <td
                          style={{
                            color:
                              visit.status === "success" ? "green" : "red",
                            fontWeight: "600",
                          }}
                        >
                          {visit.status.charAt(0).toUpperCase() +
                            visit.status.slice(1)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="20"
                        className="text-center text-danger fw-bold py-4"
                      >
                        No rejected withdrawal requests found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* PAGINATION */}
              <div className="d-flex justify-content-end mt-3">
                {visits.length > 0 && totalPages > 1 && (
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index + 1}
                        active={index + 1 === currentPage}
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
              </div>
            </div>
          </div>

          {/* Footer Copyright */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>
    </>
  );
}

export default WithdrawalStatusRejectedLists;